import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, AlertCircle } from 'lucide-react';
import { useToast } from './ToastContext';
import { useScrollLock } from '../hooks/useScrollLock';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

interface ErrorNotification {
  id: string;
  title: string;
  message: string;
  severity: ErrorSeverity;
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  }>;
  onClose?: () => void;
}

interface ErrorNotificationContextValue {
  showError: (message: string, options?: { title?: string; duration?: number }) => void;
  showWarning: (message: string, options?: { title?: string; duration?: number }) => void;
  showInfo: (message: string, options?: { title?: string; duration?: number }) => void;
  showSuccess: (message: string, options?: { title?: string; duration?: number }) => void;
  showCriticalError: (message: string, options: { 
    title?: string; 
    actions?: ErrorNotification['actions'];
    onClose?: () => void;
  }) => void;
  closeCriticalError: () => void;
}

const ErrorNotificationContext = createContext<ErrorNotificationContextValue | undefined>(undefined);

// Critical Error Modal Component
const CriticalErrorModal: React.FC<{
  error: ErrorNotification | null;
  onClose: () => void;
}> = ({ error, onClose }) => {
  useScrollLock({ isLocked: !!error, preserveScrollbarWidth: true });

  const getIcon = () => {
    switch (error?.severity) {
      case 'critical':
        return <AlertTriangle size={48} className="text-red-500" />;
      default:
        return <AlertCircle size={48} className="text-red-500" />;
    }
  };

  const getBackgroundColor = () => {
    return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
  };

  if (!error) return null;

  return (
    <AnimatePresence>
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`w-full max-w-md bg-white dark:bg-dark-50 rounded-2xl shadow-2xl border ${getBackgroundColor()} overflow-hidden`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="error-modal-title"
            aria-describedby="error-modal-message"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-4">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 p-3 rounded-full bg-red-100 dark:bg-red-900/30">
                  {getIcon()}
                </div>
                <div>
                  <h2 
                    id="error-modal-title" 
                    className="text-xl font-bold text-neutral-900 dark:text-dark-900"
                  >
                    {error.title || 'Critical Error'}
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-dark-100 text-neutral-500 dark:text-dark-400 transition-colors"
                aria-label="Close error dialog"
              >
                <X size={20} />
              </button>
            </div>

            {/* Message */}
            <div className="px-6 py-2">
              <p 
                id="error-modal-message" 
                className="text-neutral-700 dark:text-dark-700 leading-relaxed"
              >
                {error.message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 pt-4 justify-end">
              {error.actions?.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    action.onClick();
                    if (!action.variant || action.variant !== 'primary') {
                      onClose();
                    }
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    action.variant === 'danger'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : action.variant === 'secondary'
                      ? 'bg-neutral-200 hover:bg-neutral-300 dark:bg-dark-200 dark:hover:bg-dark-300 text-neutral-800 dark:text-dark-800'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                  }`}
                >
                  {action.label}
                </button>
              ))}
              {!error.actions && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg font-medium bg-primary-600 hover:bg-primary-700 text-white transition-all duration-200"
                >
                  Dismiss
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const ErrorNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { showToast } = useToast();
  const [criticalError, setCriticalError] = useState<ErrorNotification | null>(null);

  const generateId = () => `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Non-blocking errors (snackbar/toast)
  const showError = useCallback((message: string, options?: { title?: string; duration?: number }) => {
    showToast(message, 'error', { 
      title: options?.title || 'Error', 
      duration: options?.duration || 5000 
    });
  }, [showToast]);

  const showWarning = useCallback((message: string, options?: { title?: string; duration?: number }) => {
    showToast(message, 'warning', { 
      title: options?.title || 'Warning', 
      duration: options?.duration || 4000 
    });
  }, [showToast]);

  const showInfo = useCallback((message: string, options?: { title?: string; duration?: number }) => {
    showToast(message, 'info', { 
      title: options?.title || 'Info', 
      duration: options?.duration || 3000 
    });
  }, [showToast]);

  const showSuccess = useCallback((message: string, options?: { title?: string; duration?: number }) => {
    showToast(message, 'success', { 
      title: options?.title || 'Success', 
      duration: options?.duration || 3000 
    });
  }, [showToast]);

  // Critical errors (modal)
  const showCriticalError = useCallback((message: string, options: {
    title?: string;
    actions?: ErrorNotification['actions'];
    onClose?: () => void;
  }) => {
    const id = generateId();
    setCriticalError({
      id,
      title: options.title || 'Critical Error',
      message,
      severity: 'critical',
      actions: options.actions,
      onClose: options.onClose,
    });
  }, []);

  const closeCriticalError = useCallback(() => {
    if (criticalError?.onClose) {
      criticalError.onClose();
    }
    setCriticalError(null);
  }, [criticalError]);

  const value: ErrorNotificationContextValue = {
    showError,
    showWarning,
    showInfo,
    showSuccess,
    showCriticalError,
    closeCriticalError,
  };

  return (
    <ErrorNotificationContext.Provider value={value}>
      {children}
      <CriticalErrorModal 
        error={criticalError} 
        onClose={closeCriticalError} 
      />
    </ErrorNotificationContext.Provider>
  );
};

export const useErrorNotification = () => {
  const context = useContext(ErrorNotificationContext);
  if (context === undefined) {
    throw new Error('useErrorNotification must be used within an ErrorNotificationProvider');
  }
  return context;
};

// Convenience hook for handling async errors
export const useErrorHandler = () => {
  const { showError, showCriticalError, showWarning } = useErrorNotification();

  const handleAsyncError = useCallback(async <T,>(
    promise: Promise<T>,
    options?: {
      errorMessage?: string;
      critical?: boolean;
      onError?: (error: Error) => void;
    }
  ): Promise<T | null> => {
    try {
      return await promise;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      
      if (options?.critical) {
        showCriticalError(options.errorMessage || err.message, {
          title: 'Operation Failed',
        });
      } else {
        showError(options?.errorMessage || err.message);
      }
      
      options?.onError?.(err);
      return null;
    }
  }, [showError, showCriticalError]);

  return { handleAsyncError, showError, showCriticalError, showWarning };
};
